/**
 * Scenario access rules on the SE-8354 contract (SE-8356, PV16 / PRD-5.5,
 * PRD-6.2, grooming D09 / D15).
 *
 * `ScenarioModel.myRole` is what the backend resolved for the caller — OWNER,
 * EDITOR (also what a MANAGER+ reads on a legacy no-owner scenario) or VIEWER.
 * These helpers turn that one field into the affordances each surface shows,
 * without inventing a role the API did not send: while `myRole` is unknown
 * (query loading or failed) the surfaces FAIL CLOSED — the row's own owner
 * keeps the owner shape, a legacy no-owner scenario keeps today's MANAGER+
 * shape and the claim path, and every other scenario reads as a VIEWER until
 * the role resolves. The backend stays the rule and any refusal it sends is
 * rendered verbatim.
 * @module features/squad-planning/utils/scenarioAccess
 */

import { messagesOf } from "@/features/squad-planning/utils/classifyClaimOwnershipError";
import { collectGraphQLErrors } from "@/features/squad-planning/utils/collectGraphQLErrors";
import { SCENARIO_REVISION_CONFLICT_CODE } from "@/features/squad-planning/utils/scenarioRevisionConflict";
import type { RoleName } from "@/generated/graphql";

/** `ScenarioRole` as the wire carries it (string form of the generated enum). */
export type ScenarioRoleValue = "OWNER" | "EDITOR" | "VIEWER";

/** PRD-5.5 note under the WHO HAS ACCESS list of a scenario. */
export const SCENARIO_SHARE_NOTE =
  "Editors can add, remove and reorder players. Only the owner can rename, delete or change sharing.";

/** Caption when the invite search matches only Tier 3 members (D15; frame copy, SE-8415). */
export const TIER_3_INVITE_CAPTION = "Scouts can't access scenarios";

/** Fallback when a sharing rejection carries no readable message. */
export const SHARING_FAILED_MESSAGE = "Failed to update sharing";

/** Copy for a Financials switch refused because the scenario changed. */
export const FINANCIALS_CONFLICT_MESSAGE =
  "The scenario changed · Reloaded, try again";

/** Copy when the re-read after a refused switch could not complete. */
export const FINANCIALS_CONFLICT_RELOAD_FAILED_MESSAGE =
  "The scenario changed · Couldn’t reload it, try again";

/**
 * Organization roles the backend maps ABOVE Tier 3 (`compliance-role-tier.ts`:
 * OWNER → TIER_1, ADMIN / MANAGER → TIER_2). Everything else — MEMBER, a
 * membership with no role, an unknown name — falls through to TIER_3 there,
 * and a membership with no role is the majority of dev's rows, not an edge.
 */
const ABOVE_TIER_3_ORGANIZATION_ROLES: ReadonlySet<string> =
  new Set<`${RoleName}`>(["OWNER", "ADMIN", "MANAGER"]);

/** What a surface may offer for the scenario in view. */
export interface ScenarioPermissions {
  /** Add / Remove / formation change (OWNER, EDITOR, legacy MANAGER+). */
  readonly canEdit: boolean;
  /** Rename / delete (OWNER; MANAGER+ on a legacy no-owner scenario, as today). */
  readonly canManage: boolean;
  /** Open the Share dialog (OWNER only; a legacy scenario is claimed first). */
  readonly canShare: boolean;
}

/** The fields the rules read. */
export interface ScenarioAccessInput {
  /** `myRole` as the API sent it, or null while it has not said. */
  readonly myRole: ScenarioRoleValue | null;
  /** `isLegacyNoOwner` from the list row (SE-8340). */
  readonly isLegacyNoOwner: boolean;
  /**
   * Whether the row's `ownerUserId` is the signed-in user — known from the
   * list row alone, so the owner keeps their affordances while the role read
   * is still unresolved. Defaults to false.
   */
  readonly isOwnedByMe?: boolean;
}

/**
 * Resolves the affordances for a scenario from the role the API reported.
 *
 * While the role is UNRESOLVED (the access read is loading or failed) the
 * rule fails closed: a scenario the row says I own keeps the owner shape, a
 * legacy no-owner scenario keeps today's MANAGER+ shape and the claim path,
 * and any other scenario gets the VIEWER shape — hidden, not disabled —
 * until the role resolves. The backend remains the rule either way.
 * @param input - The caller's role, the legacy flag and whether the row is mine
 * @returns What the surfaces may offer
 */
export function resolveScenarioPermissions(
  input: ScenarioAccessInput
): ScenarioPermissions {
  switch (input.myRole) {
    case "OWNER":
      return { canEdit: true, canManage: true, canShare: true };
    case "EDITOR":
      // SE-8526: a claim writes the owner onto the list row, but the role
      // read still says EDITOR until it is re-read — the claim mutation does
      // not return `myRole`. The row is authoritative on who owns it, so the
      // new owner gets the owner's affordances (Share opens) at once.
      if (input.isOwnedByMe === true) {
        return { canEdit: true, canManage: true, canShare: true };
      }
      // A legacy no-owner scenario reports EDITOR for MANAGER+, whose rename
      // and delete stay as they are today (SE-8354 contract); a granted
      // editor on an owned scenario never renames or deletes (PRD-6.2).
      return {
        canEdit: true,
        canManage: input.isLegacyNoOwner,
        canShare: false,
      };
    case "VIEWER":
      return { canEdit: false, canManage: false, canShare: false };
    default:
      if (input.isOwnedByMe === true) {
        return { canEdit: true, canManage: true, canShare: true };
      }
      if (input.isLegacyNoOwner) {
        // Today's MANAGER+ behaviour; the claim badge is the entry, not Share.
        return { canEdit: true, canManage: true, canShare: false };
      }
      return { canEdit: false, canManage: false, canShare: false };
  }
}

/** Inputs for {@link sharedByLine}. */
export interface SharedByInput extends ScenarioAccessInput {
  /** The owner's display name when resolvable, else null. */
  readonly ownerName: string | null;
}

/**
 * The "Shared with you by {owner} · Can view/edit" line an invitee sees on
 * the Pitch (frame copy, SE-8415).
 * @param input - Role, legacy flag and the owner's name
 * @returns The line, or null for the owner / a legacy scenario / no answer
 */
export function sharedByLine(input: SharedByInput): string | null {
  if (
    input.isLegacyNoOwner ||
    (input.myRole !== "EDITOR" && input.myRole !== "VIEWER")
  ) {
    return null;
  }
  const who =
    input.ownerName == null
      ? "Shared with you"
      : `Shared with you by ${input.ownerName}`;
  const can = input.myRole === "EDITOR" ? "Can edit" : "Can view";
  return `${who} · ${can}`;
}

/**
 * Whether an organization role resolves to TIER_3, which cannot be invited to
 * a scenario (PRD-5.5 / D15). Mirrors the backend's fail-closed mapping so the
 * invite pool never offers someone `createUserScenario` will refuse with
 * `SCENARIO_INVITEE_TIER_3` — the backend refusal stays the rule and is still
 * rendered verbatim if reached; this filter is the convenience.
 * @param roleName - The member's organization role, or null when the
 * membership carries none
 * @returns `true` for MEMBER, for a missing role and for an unknown role
 */
export const isTier3OrganizationRole = (
  roleName: `${RoleName}` | null | undefined
): boolean =>
  roleName == null || !ABOVE_TIER_3_ORGANIZATION_ROLES.has(roleName);

/** How a sharing write was refused. */
export interface ScenarioSharingRejection {
  readonly kind: "revision-conflict" | "other";
  /** The `extensions.code` the backend sent, or null. */
  readonly code: string | null;
  /** The backend's own wording, verbatim. */
  readonly message: string;
}

const textOf = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

/**
 * Classifies a sharing / Financials-switch rejection without rewording it.
 * Only `SCENARIO_REVISION_CONFLICT` gets special handling (refetch and
 * re-offer, never an automatic retry); everything else is shown as sent.
 * @param error - The value the mutation rejected with
 * @returns The rejection kind, code and message
 */
export function classifyScenarioSharingError(
  error: unknown
): ScenarioSharingRejection {
  const entries = collectGraphQLErrors(error);
  const codes = entries.flatMap(entry =>
    typeof entry.extensions?.code === "string" ? [entry.extensions.code] : []
  );
  // The conflict wins wherever it sits in the list; else the first code.
  const code = codes.includes(SCENARIO_REVISION_CONFLICT_CODE)
    ? SCENARIO_REVISION_CONFLICT_CODE
    : (codes[0] ?? null);
  const topLevel = textOf((error as { message?: unknown } | null)?.message);
  const message =
    entries.flatMap(messagesOf)[0] ??
    (topLevel.length > 0 ? topLevel : SHARING_FAILED_MESSAGE);
  return {
    kind:
      code === SCENARIO_REVISION_CONFLICT_CODE ? "revision-conflict" : "other",
    code,
    message,
  };
}
